import { useEffect, useMemo } from 'react';
import { LocalStorageKeys } from 'src/enums/ELocalStorage';
import Favicon from '@/assets/images/favicon.ico';
import { BRANDED_DOMAINS, UNBRANDED_DOMAINS } from 'src/enums/ERoutes';

interface UseFaviconProps {
	isUnbrandedPath: boolean;
	resellerCode?: string | null;
	resellerLogo?: Blob;
	refetchReseller?: () => void;
}

export const useFavicon = ({
	isUnbrandedPath,
	resellerCode,
	resellerLogo,
	refetchReseller,
}: UseFaviconProps) => {
	const domain = window.location.hostname;

	const isBrandedDomain = BRANDED_DOMAINS.includes(domain);
	const isCustomDomain = !BRANDED_DOMAINS.includes(domain) || !UNBRANDED_DOMAINS.includes(domain);
	const resellerLogoUrl = useMemo(() => {
		if (!resellerLogo) {
			return;
		}
		return URL.createObjectURL(resellerLogo);
	}, [resellerLogo]);

	const convertToFavicon = async (imageSource: string | Blob): Promise<string | undefined> => {
		try {
			const img = new Image();
			const imageLoadPromise = new Promise((resolve, reject) => {
				img.onload = () => resolve(img);
				img.onerror = () => reject(new Error('Failed to load image'));
			});

			if (typeof imageSource === 'string') {
				img.crossOrigin = 'anonymous';
				img.src = imageSource;
			} else {
				const reader = new FileReader();
				reader.onload = (e: ProgressEvent<FileReader>) => {
					if (e.target?.result) {
						img.src = e.target.result as string;
					}
				};
				reader.readAsDataURL(imageSource);
			}

			await imageLoadPromise;

			const canvas = document.createElement('canvas');
			canvas.width = 48;
			canvas.height = 48;
			const ctx = canvas.getContext('2d');
			ctx?.drawImage(img, 0, 0, 48, 48);

			const faviconUrl = canvas.toDataURL('image/png');
			localStorage.setItem(LocalStorageKeys.FAVICON_URL, faviconUrl);
			return faviconUrl;
		} catch (err) {
			console.error('Error converting image:', err);
			return undefined;
		}
	};

	const setFavicon = (url: string) => {
		let link = document.querySelector("link[rel*='icon']") as HTMLLinkElement;
		if (!link) {
			link = document.createElement('link');
			link.rel = 'icon';
			document.head.appendChild(link);
		}
		link.href = url;
	};

	const setFaviconToNone = () => {
		const emptyDataUrl = 'data:,';
		setFavicon(emptyDataUrl);
	};

	// Handle initial favicon setup or reseller refetch
	useEffect(() => {
		if (isBrandedDomain) {
			setFavicon(Favicon);
		} else if (isCustomDomain && !resellerCode) {
			refetchReseller?.();
		} else if (!resellerLogo) {
			setFaviconToNone();
		}
	}, [isBrandedDomain, resellerCode, resellerLogo, isUnbrandedPath]);

	// Handle reseller logo changes
	useEffect(() => {
		const updateFavicon = async () => {
			if (!resellerLogoUrl) {
				return;
			}

			let faviconUrl = localStorage.getItem(LocalStorageKeys.FAVICON_URL) || '';
			if (!faviconUrl) {
				faviconUrl = (await convertToFavicon(resellerLogoUrl)) || '';
			}

			setFavicon(faviconUrl || Favicon);
		};

		updateFavicon();
	}, [resellerLogoUrl]);

	return null;
};
